import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

// useOAuthPopup — the ONE way this app opens a Meta OAuth popup.
//
// Why a hook and not six copy-pasted openers:
//  1. `window.opener` is severed by COOP after the cross-origin OAuth round trip, so the popup
//     cannot call back into us. The old code worked around that with window.location.reload(),
//     which threw the user out of whatever modal they were in. It also leaked its polling
//     interval on every unmount.
//  2. The popup now leaves its outcome in localStorage under `vesta_oauth_result` (written by
//     Facebook::OauthController#redirect_to_appropriate_page just before window.close()). We
//     read it, clear it, toast it, and hand control back to the caller's onSettled() — which
//     re-fetches state in place. No reload, no alert(), modal stays open.

export const OAUTH_RESULT_KEY = 'vesta_oauth_result';

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;
const POLL_MS = 1000;

// Reads and CLEARS the popup's result. Clearing matters: a stale key would toast the previous
// connect's message on the next popup that happens to close without writing one.
export const readOAuthResult = () => {
  try {
    const raw = window.localStorage.getItem(OAUTH_RESULT_KEY);
    if (!raw) return null;
    window.localStorage.removeItem(OAUTH_RESULT_KEY);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_e) {
    return null;
  }
};

const useOAuthPopup = (onSettled) => {
  // Kept in a ref so the returned opener is stable across renders while still calling the
  // freshest callback (onSettled closes over component state).
  const settledRef = useRef(onSettled);
  useEffect(() => { settledRef.current = onSettled; }, [onSettled]);

  const timersRef = useRef(new Set());
  useEffect(() => () => {
    timersRef.current.forEach((t) => clearInterval(t));
    timersRef.current.clear();
  }, []);

  return useCallback((url, windowNamePrefix) => {
    const left = (window.innerWidth - POPUP_WIDTH) / 2;
    const top = (window.innerHeight - POPUP_HEIGHT) / 2;

    const popup = window.open(
      url,
      `${windowNamePrefix}_${Date.now()}`,
      `popup=yes,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},top=${top},left=${left},resizable=yes,scrollbars=yes,toolbar=0,location=0,menubar=0,status=0`
    );

    // Popup blocker: fall back to the same-tab flow. A toast, never an alert() — an alert here
    // blocks the redirect behind a dialog the user has to dismiss first.
    if (!popup) {
      toast.error('Please allow popups in your browser. Redirecting…');
      window.location.href = url;
      return;
    }

    const timer = setInterval(() => {
      if (!popup.closed) return;
      clearInterval(timer);
      timersRef.current.delete(timer);

      const result = readOAuthResult();
      if (result && result.message) {
        if (result.level === 'alert') toast.error(result.message);
        else toast.success(result.message);
      } else {
        // The popup may have been dismissed, or the flow may predate the result channel.
        // Either way state may have changed, so we still re-fetch — just without a claim.
        toast.success('Connection updated');
      }

      try { settledRef.current?.(); } catch (_e) { /* a callback throw must not leak here */ }
    }, POLL_MS);

    timersRef.current.add(timer);
  }, []);
};

export default useOAuthPopup;
